import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';
import {
  FabricSummarySearchResult,
  CostReportsProxyService,
  FabricTreeNodeDto,
  ProcessCostBucketDto,
} from '../../../proxies/cost-reports.proxy.service';
import { CostAnalysisStateService } from '../services/cost-analysis-state.service';
import { toSearchCommand } from '../utils/filter-command.util';
import {
  ALL_PROCESSES,
  ColorDisplay,
  CostSourceFilter,
  HierarchyContext,
  HierarchyLevel,
  OrderType,
  PROCESS_LABELS,
  ProcessDetailContext,
  ProcessKey,
  ProductionCardStatus,
  getOrderTypeLabel,
  getProductionCardStatusLabel,
  resolveColorSwatch,
} from '../models/cost-analysis.models';
import { Translation } from '../../../models/Translations';

interface VisibleDescendant {
  node: FabricTreeNodeDto;
  depth: number;
}

@Component({
  selector: 'app-fabric-summary',
  templateUrl: './fabric-summary.component.html',
  styleUrl: './fabric-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FabricSummaryComponent {
  private readonly proxy = inject(CostReportsProxyService);
  private readonly state = inject(CostAnalysisStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  readonly year = input.required<number>();
  readonly month = input.required<number>();
  readonly costSource = input.required<CostSourceFilter>();

  readonly processClick = output<ProcessDetailContext>();

  readonly result = signal<FabricSummarySearchResult | null>(null);
  readonly loading = signal(false);

  readonly pageNumber = signal(1);
  readonly pageSize = signal(10);
  readonly rowsPerPageOptions: number[] = [5, 10, 25, 50];

  readonly processes: readonly ProcessKey[] = ALL_PROCESSES;
  readonly processLabels = PROCESS_LABELS;

  readonly fabrics = computed<FabricTreeNodeDto[]>(() => this.result()?.items ?? []);

  private readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  private readonly childrenByFabric = signal<ReadonlyMap<string, FabricTreeNodeDto[]>>(new Map());
  private readonly loadingFabricIds = signal<ReadonlySet<string>>(new Set());

  readonly allFabricsExpanded = computed(() => {
    const ids = this.expandedIds();
    const fabs = this.fabrics();
    return fabs.length > 0 && fabs.every((f) => ids.has(f.id));
  });

  trackById = (_: number, fabric: FabricTreeNodeDto) => fabric.id;

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  isFabricLoading(fabricId: string): boolean {
    return this.loadingFabricIds().has(fabricId);
  }

  fabricChildren(fabric: FabricTreeNodeDto): FabricTreeNodeDto[] {
    return this.childrenByFabric().get(fabric.id) ?? [];
  }

  toggle(id: string): void {
    const next = new Set(this.expandedIds());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.expandedIds.set(next);
  }

  toggleFabric(fabric: FabricTreeNodeDto): void {
    const wasExpanded = this.isExpanded(fabric.id);
    this.toggle(fabric.id);
    if (!wasExpanded && !this.childrenByFabric().has(fabric.id)) {
      this.loadChildrenFor(fabric);
    }
  }

  toggleAllFabrics(): void {
    const allOpen = this.allFabricsExpanded();
    const next = new Set(this.expandedIds());
    for (const fabric of this.fabrics()) {
      if (allOpen) {
        next.delete(fabric.id);
      } else {
        next.add(fabric.id);
        if (!this.childrenByFabric().has(fabric.id)) {
          this.loadChildrenFor(fabric);
        }
      }
    }
    this.expandedIds.set(next);
  }

  private loadChildrenFor(fabric: FabricTreeNodeDto): void {
    const fabricCode = fabric.context.fabricCode;
    if (!fabricCode) return;
    const y = this.year();
    const m = this.month();
    if (!y || !m) return;

    const loading = new Set(this.loadingFabricIds());
    loading.add(fabric.id);
    this.loadingFabricIds.set(loading);

    this.proxy
      .expandFabric(y, m, fabricCode, this.buildCommand(), this.state.filters().onlyCompletedRolls)
      .pipe(catchError(() => of(null)))
      .subscribe((resp) => {
        const children = resp?.success && resp.data ? resp.data : [];
        const nextMap = new Map(this.childrenByFabric());
        nextMap.set(fabric.id, children);
        this.childrenByFabric.set(nextMap);

        const nextLoading = new Set(this.loadingFabricIds());
        nextLoading.delete(fabric.id);
        this.loadingFabricIds.set(nextLoading);
      });
  }

  visibleDescendants(fabric: FabricTreeNodeDto): VisibleDescendant[] {
    const children = this.childrenByFabric().get(fabric.id) ?? [];
    const out: VisibleDescendant[] = [];
    const walk = (nodes: FabricTreeNodeDto[], depth: number) => {
      for (const child of nodes) {
        out.push({ node: child, depth });
        if (child.children.length > 0 && this.expandedIds().has(child.id)) {
          walk(child.children, depth + 1);
        }
      }
    };
    walk(children, 1);
    return out;
  }

  bucketFor(node: FabricTreeNodeDto, process: ProcessKey): ProcessCostBucketDto | undefined {
    return node.byProcess?.[process];
  }

  standardSharePct(node: FabricTreeNodeDto): number {
    let totalLbs = 0;
    let stdLbs = 0;
    for (const p of ALL_PROCESSES) {
      const b = node.byProcess?.[p];
      if (!b) continue;
      totalLbs += b.totalLbs;
      stdLbs += b.standardTotalLbs;
    }
    return totalLbs > 0 ? (stdLbs / totalLbs) * 100 : 0;
  }

  totalCostPerLb(node: FabricTreeNodeDto): number {
    return ALL_PROCESSES.reduce((sum, p) => sum + (node.byProcess?.[p]?.costPerLbUsd ?? 0), 0);
  }

  hasDescription(node: FabricTreeNodeDto): boolean {
    return !!node.subLabel;
  }

  colorChip(node: FabricTreeNodeDto): ColorDisplay | undefined {
    if (node.level !== 'color') return undefined;
    const ref = node.context.colorReference;
    if (!ref) return { kind: 'uncolored' };
    const name = node.context.colorName;
    if (name) {
      return {
        kind: 'colored',
        name,
        labDipNumber: node.context.labDipNumber ?? null,
        swatch: resolveColorSwatch(name),
      };
    }
    return { kind: 'orphan', diagnostic: ref.slice(0, 8) };
  }

  workOrderCustomerName(node: FabricTreeNodeDto): string | null {
    if (node.level !== 'workOrder') return null;
    return node.context.customerName ?? null;
  }

  fabricCustomerName(node: FabricTreeNodeDto): string | null {
    if (node.context.customerName) return node.context.customerName;
    const children = this.childrenByFabric().get(node.id);
    if (!children) return null;
    return this.firstCustomerName(children);
  }

  private firstCustomerName(nodes: FabricTreeNodeDto[]): string | null {
    for (const node of nodes) {
      if (node.context.customerName) return node.context.customerName;
      const fromChildren = this.firstCustomerName(node.children ?? []);
      if (fromChildren) return fromChildren;
    }
    return null;
  }

  workOrderTypeLabel(node: FabricTreeNodeDto): string | null {
    if (node.level !== 'workOrder') return null;
    const t = node.context.workOrderType;
    if (t === null || t === undefined) return null;
    const lang = (this.translateService.currentLang || 'en') as keyof Translation;
    return getOrderTypeLabel(t as OrderType)[lang] ?? null;
  }

  workOrderTypeStyle(node: FabricTreeNodeDto): string {
    const t = node.context.workOrderType;
    if (t === null || t === undefined) return 'bg-slate-100 text-slate-700';
    switch (t as OrderType) {
      case OrderType.Sample:
        return 'bg-amber-100 text-amber-800';
      case OrderType.Bulk:
        return 'bg-emerald-100 text-emerald-800';
      case OrderType.Greige:
        return 'bg-sky-100 text-sky-800';
      case OrderType.ShortFall:
        return 'bg-rose-100 text-rose-800';
      case OrderType.Linked:
        return 'bg-violet-100 text-violet-800';
      case OrderType.BGrade:
        return 'bg-fuchsia-100 text-fuchsia-800';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  }

  pcStatusLabel(status: number | null | undefined): string | null {
    if (status === null || status === undefined) return null;
    const lang = (this.translateService.currentLang || 'en') as keyof Translation;
    return getProductionCardStatusLabel(status as ProductionCardStatus)[lang] ?? null;
  }

  processCostMode(node: FabricTreeNodeDto, process: ProcessKey): 'Real' | 'Standard' | 'Mixed' | null {
    if (node.level !== 'productionCard' && node.level !== 'roll') return null;
    const b = node.byProcess?.[process];
    if (!b || b.totalCostUsd <= 0) return null;
    const hasReal = b.realTotalCostUsd > 0;
    const hasStd = b.standardTotalCostUsd > 0;
    if (hasReal && hasStd) return 'Mixed';
    if (hasReal) return 'Real';
    return 'Standard';
  }

  onProcessClick(node: FabricTreeNodeDto, process: ProcessKey): void {
    const ctx: HierarchyContext = {
      fabricCode: node.context.fabricCode ?? undefined,
      fabricDescription: node.context.fabricDescription ?? undefined,
      colorReference: node.context.colorReference,
      workOrderReference: node.context.workOrderReference ?? undefined,
      workOrderNumber: node.context.workOrderNumber ?? undefined,
      productionCardReference: node.context.productionCardReference ?? undefined,
      productionCardNumber: node.context.productionCardNumber ?? undefined,
      knittingOrderReference: node.context.knittingOrderReference ?? undefined,
      knittingOrderNumber: node.context.knittingOrderNumber ?? undefined,
      rollNumber: node.context.rollNumber ?? undefined,
    };
    this.processClick.emit({
      process,
      level: node.level as HierarchyLevel,
      context: ctx,
      year: this.year(),
      month: this.month(),
    });
  }

  onPaginate(event: { first?: number; rows?: number }): void {
    const newSize = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    const newPage = Math.floor(first / newSize) + 1;
    if (newSize !== this.pageSize()) this.pageSize.set(newSize);
    if (newPage !== this.pageNumber()) this.pageNumber.set(newPage);
  }

  private readonly searchTrigger$ = new Subject<void>();

  constructor() {
    this.searchTrigger$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const y = this.year();
          const m = this.month();
          if (!y || !m) return of(null);
          const cmd = this.buildCommand();
          const page = this.pageNumber();
          const size = this.pageSize();
          this.loading.set(true);
          return this.proxy.searchFabricSummary(y, m, cmd, page, size, this.state.filters().onlyCompletedRolls).pipe(
            catchError(() => of(null)),
            finalize(() => this.loading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        this.result.set(resp && resp.success && resp.data ? resp.data : null);
        this.expandedIds.set(new Set());
        this.childrenByFabric.set(new Map());
        this.loadingFabricIds.set(new Set());
      });

    // Deliberately does NOT track year()/month()/costSource(): those only
    // take effect when the page's Apply/Reset bumps reloadRequested (see
    // CostAnalysisStateService), so picking a period or cost source alone
    // never fires a search on its own. filters()/pageNumber()/pageSize() stay
    // live/instant, matching existing UX.
    effect(() => {
      this.state.filters();
      this.state.reloadRequested();
      this.pageNumber();
      this.pageSize();
      this.searchTrigger$.next();
    });
  }

  private buildCommand() {
    return toSearchCommand(this.costSource(), this.state.filters());
  }
}
